import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  TextField,
  Typography,
  Alert,
  Divider,
  IconButton,
  InputAdornment,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useAuth } from '../contexts/AuthContext';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      navigate('/app');
    } else {
      setError(result.error || 'Ungültige E-Mail oder Passwort');
    }
    
    setIsLoading(false);
  };

  return (
    <Box
      className="industrial-bg"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Card
          sx={{
            background: 'linear-gradient(135deg, rgba(16, 42, 67, 0.95), rgba(36, 59, 83, 0.95))',
            border: '1px solid rgba(0, 180, 216, 0.2)',
            backdropFilter: 'blur(20px)',
          }}
        >
          <CardContent sx={{ p: 5 }}>
            {/* Logo */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #00b4d8, #14b8a6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                  boxShadow: '0 8px 32px rgba(0, 180, 216, 0.3)',
                }}
              >
                <TimelineIcon sx={{ color: 'white', fontSize: 36 }} />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#e2e8f0' }}>
                Willkommen zurück
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Melden Sie sich bei Ihrem DZ Content Factory Konto an
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="E-Mail Adresse"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                sx={{ mb: 3 }}
              />

              <TextField
                fullWidth
                label="Passwort"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                sx={{ mb: 3 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={isLoading}
                sx={{
                  py: 1.5,
                  background: 'linear-gradient(135deg, #00b4d8, #14b8a6)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #00d9ff, #2dd4bf)',
                  },
                }}
              >
                {isLoading ? 'Wird angemeldet...' : 'Anmelden'}
              </Button>
            </form>

            <Box sx={{ textAlign: 'right', mt: -2, mb: 2 }}>
              <Link
                to="/forgot-password"
                style={{ color: '#00b4d8', textDecoration: 'none', fontSize: '0.875rem' }}
              >
                Passwort vergessen?
              </Link>
            </Box>

            <Divider sx={{ my: 3 }}>
              <Typography variant="caption" color="text.secondary">
                oder
              </Typography>
            </Divider>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Noch kein Konto?{' '}
                <Link
                  to="/register"
                  style={{
                    color: '#00b4d8',
                    textDecoration: 'none',
                    fontWeight: 600,
                  }}
                >
                  Jetzt registrieren
                </Link>
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Link
            to="/"
            style={{
              color: '#94a3b8',
              textDecoration: 'none',
              fontSize: '0.875rem',
            }}
          >
            ← Zurück zur Startseite
          </Link>
        </Box>
      </Container>
    </Box>
  );
}

export function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }

    if (password.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    setIsLoading(true);

    const result = await register(email, password, name);
    
    if (result.success) {
      navigate('/app');
    } else {
      setError(result.error || 'Registrierung fehlgeschlagen');
    }
    
    setIsLoading(false);
  };

  return (
    <Box
      className="industrial-bg"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Card
          sx={{
            background: 'linear-gradient(135deg, rgba(16, 42, 67, 0.95), rgba(36, 59, 83, 0.95))',
            border: '1px solid rgba(0, 180, 216, 0.2)',
            backdropFilter: 'blur(20px)',
          }}
        >
          <CardContent sx={{ p: 5 }}>
            {/* Logo */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #00b4d8, #14b8a6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                  boxShadow: '0 8px 32px rgba(0, 180, 216, 0.3)',
                }}
              >
                <TimelineIcon sx={{ color: 'white', fontSize: 36 }} />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#e2e8f0' }}>
                Konto erstellen
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Starten Sie kostenlos mit 3 Testanfragen
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                sx={{ mb: 3 }}
              />

              <TextField
                fullWidth
                label="E-Mail Adresse"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                sx={{ mb: 3 }}
              />

              <TextField
                fullWidth
                label="Passwort"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                helperText="Mindestens 8 Zeichen"
                sx={{ mb: 3 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Passwort bestätigen"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={isLoading}
                sx={{
                  py: 1.5,
                  background: 'linear-gradient(135deg, #00b4d8, #14b8a6)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #00d9ff, #2dd4bf)',
                  },
                }}
              >
                {isLoading ? 'Wird registriert...' : 'Kostenlos registrieren'}
              </Button>
            </form>

            <Divider sx={{ my: 3 }}>
              <Typography variant="caption" color="text.secondary">
                oder
              </Typography>
            </Divider>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Bereits ein Konto?{' '}
                <Link
                  to="/login"
                  style={{
                    color: '#00b4d8',
                    textDecoration: 'none',
                    fontWeight: 600,
                  }}
                >
                  Jetzt anmelden
                </Link>
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Link
            to="/"
            style={{
              color: '#94a3b8',
              textDecoration: 'none',
              fontSize: '0.875rem',
            }}
          >
            ← Zurück zur Startseite
          </Link>
        </Box>
      </Container>
    </Box>
  );
}

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage('Falls ein Konto mit dieser E-Mail existiert, wurde ein Link zum Zurücksetzen des Passworts versendet.');
      } else {
        setError(data.error || 'Ein Fehler ist aufgetreten.');
      }
    } catch {
      setError('Server nicht erreichbar. Bitte versuchen Sie es später erneut.');
    }
    setIsLoading(false);
  };

  return (
    <Box
      className="industrial-bg"
      sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}
    >
      <Container maxWidth="sm">
        <Card
          sx={{
            background: 'linear-gradient(135deg, rgba(16, 42, 67, 0.95), rgba(36, 59, 83, 0.95))',
            border: '1px solid rgba(0, 180, 216, 0.2)',
            backdropFilter: 'blur(20px)',
          }}
        >
          <CardContent sx={{ p: 5 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                sx={{
                  width: 64, height: 64, borderRadius: 2,
                  background: 'linear-gradient(135deg, #00b4d8, #14b8a6)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  mx: 'auto', mb: 2, boxShadow: '0 8px 32px rgba(0, 180, 216, 0.3)',
                }}
              >
                <TimelineIcon sx={{ color: 'white', fontSize: 36 }} />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#e2e8f0' }}>
                Passwort zurücksetzen
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Geben Sie Ihre E-Mail-Adresse ein – wir senden Ihnen einen Reset-Link
              </Typography>
            </Box>

            {message && <Alert severity="success" sx={{ mb: 3 }}>{message}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {!message && (
              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="E-Mail Adresse"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  sx={{ mb: 3 }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={isLoading}
                  sx={{
                    py: 1.5,
                    background: 'linear-gradient(135deg, #00b4d8, #14b8a6)',
                    '&:hover': { background: 'linear-gradient(135deg, #00d9ff, #2dd4bf)' },
                  }}
                >
                  {isLoading ? 'Wird gesendet...' : 'Reset-Link senden'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Link to="/login" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: '0.875rem' }}>
            ← Zurück zum Login
          </Link>
        </Box>
      </Container>
    </Box>
  );
}

export function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const token = new URLSearchParams(window.location.search).get('token') || '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwörter stimmen nicht überein.');
      return;
    }
    if (password.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage('Ihr Passwort wurde erfolgreich zurückgesetzt.');
        setTimeout(() => navigate('/login'), 3000);
      } else {
        setError(data.error || 'Ungültiger oder abgelaufener Link.');
      }
    } catch {
      setError('Server nicht erreichbar. Bitte versuchen Sie es später erneut.');
    }
    setIsLoading(false);
  };

  if (!token) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Alert severity="error">Ungültiger Reset-Link. Bitte fordern Sie einen neuen an.</Alert>
      </Box>
    );
  }

  return (
    <Box
      className="industrial-bg"
      sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}
    >
      <Container maxWidth="sm">
        <Card
          sx={{
            background: 'linear-gradient(135deg, rgba(16, 42, 67, 0.95), rgba(36, 59, 83, 0.95))',
            border: '1px solid rgba(0, 180, 216, 0.2)',
            backdropFilter: 'blur(20px)',
          }}
        >
          <CardContent sx={{ p: 5 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                sx={{
                  width: 64, height: 64, borderRadius: 2,
                  background: 'linear-gradient(135deg, #00b4d8, #14b8a6)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  mx: 'auto', mb: 2, boxShadow: '0 8px 32px rgba(0, 180, 216, 0.3)',
                }}
              >
                <TimelineIcon sx={{ color: 'white', fontSize: 36 }} />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#e2e8f0' }}>
                Neues Passwort setzen
              </Typography>
            </Box>

            {message && <Alert severity="success" sx={{ mb: 3 }}>{message} Sie werden weitergeleitet...</Alert>}
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {!message && (
              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Neues Passwort"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  helperText="Mindestens 8 Zeichen"
                  sx={{ mb: 3 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label="Passwort bestätigen"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  sx={{ mb: 3 }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={isLoading}
                  sx={{
                    py: 1.5,
                    background: 'linear-gradient(135deg, #00b4d8, #14b8a6)',
                    '&:hover': { background: 'linear-gradient(135deg, #00d9ff, #2dd4bf)' },
                  }}
                >
                  {isLoading ? 'Wird gespeichert...' : 'Passwort speichern'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Link to="/login" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: '0.875rem' }}>
            ← Zurück zum Login
          </Link>
        </Box>
      </Container>
    </Box>
  );
}
